import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { membersApi } from '../services/api';

const MemberView = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [downloadingIndex, setDownloadingIndex] = useState(null);

  // Editable form state
  const [formData, setFormData] = useState({
    firstName: '',
    middleName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    areaOfSpecialty: '',
    membershipType: '',
    streetAddress: '',
    permanentAddress: '',
    currentPractice: '',
    yearQualifiedMBBS: '',
    mdcnRegistrationNumber: ''
  });

  useEffect(() => {
    fetchMemberDetails();
  }, [id]);

  // Safe field extractor helper
  const getField = (data, ...keys) => {
    if (!data) return '';
    for (const key of keys) {
      if (data[key] !== undefined && data[key] !== null && data[key] !== '') {
        return data[key];
      }
    }
    return '';
  };

  const fetchMemberDetails = async () => {
    try {
      setLoading(true);
      const response = await membersApi.getById(id);
      const data = response.data?.member || response.data || {};
      setMember(data);

      const extractedYear = getField(
        data,
        'yearQualifiedMBBS',
        'yearqualifiedmbbs',
        'year_qualified_mbbs',
        'qualificationYear',
        'qualificationyear',
        'mbbsYear',
        'mbbs_year'
      );

      setFormData({
        firstName: getField(data, 'firstName', 'firstname', 'first_name'),
        middleName: getField(data, 'middleName', 'middlename', 'middle_name'),
        lastName: getField(data, 'lastName', 'lastname', 'last_name'),
        email: getField(data, 'email'),
        phoneNumber: getField(data, 'phoneNumber', 'phonenumber', 'phone_number', 'phone'),
        areaOfSpecialty: getField(data, 'areaOfSpecialty', 'areaofspecialty', 'area_of_specialty', 'specialty'),
        membershipType: getField(data, 'membershipType', 'membershiptype', 'membership_type') || 'new',
        streetAddress: getField(data, 'streetAddress', 'streetaddress', 'street_address'),
        permanentAddress: getField(data, 'permanentAddress', 'permanentaddress', 'permanent_address'),
        currentPractice: getField(data, 'currentPractice', 'currentpractice', 'current_practice'),
        yearQualifiedMBBS: extractedYear,
        mdcnRegistrationNumber: getField(data, 'mdcnRegistrationNumber', 'mdcnregistrationnumber', 'mdcn_registration_number', 'mdnRegistration')
      });

      setError('');
    } catch (err) {
      console.error('Fetch member error:', err);
      setError('Failed to load member details.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError('');
      setSuccessMessage('');

      await membersApi.update(id, formData);
      setSuccessMessage('Member details updated successfully!');
      setIsEditing(false);
      fetchMemberDetails(); // Refresh data
    } catch (err) {
      console.error('Update error:', err);
      setError('Failed to update member details.');
    } finally {
      setSaving(false);
    }
  };

  // Helper to format Cloudinary & local server URLs cleanly
  const formatUrl = (rawPath) => {
    if (!rawPath) return '';
    const API_BASE_URL = 'https://naus-backend.onrender.com';

    if (rawPath.startsWith('http://') || rawPath.startsWith('https://')) {
      if (rawPath.includes('res.cloudinary.com') && !rawPath.includes('/fl_attachment/')) {
        return rawPath.replace('/upload/', '/upload/fl_attachment/');
      }
      return rawPath;
    }

    let cleaned = rawPath.replace(/^\/+/, '');
    if (cleaned.startsWith('uploads/')) {
      cleaned = cleaned.replace(/^uploads\//, '');
    }
    return `${API_BASE_URL}/uploads/${cleaned}`;
  };

  // Programmatic blob download handler
  const triggerDownload = async (url, filename, index) => {
    try {
      setDownloadingIndex(index);
      const targetUrl = formatUrl(url);

      const response = await fetch(targetUrl, { mode: 'cors' });
      if (!response.ok) throw new Error('Network response was not ok');

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename || 'certificate';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.warn('Direct blob fetch failed, opening in new tab:', err);
      window.open(formatUrl(url), '_blank', 'noopener,noreferrer');
    } finally {
      setDownloadingIndex(null);
    }
  };

  // Extract certificate documents for rendering
  const getDocuments = () => {
    if (!member) return [];
    const docs = [];

    const mbbsCert = getField(member, 'mbbsCertificate', 'mbbscertificate', 'mbbs_certificate');
    if (mbbsCert) {
      docs.push({
        filename: mbbsCert.split('/').pop() || 'MBBS_Certificate.pdf',
        rawPath: mbbsCert,
        type: 'MBBS Certificate'
      });
    }

    const fellowshipCert = getField(member, 'fellowshipCertificate', 'fellowshipcertificate', 'fellowship_certificate');
    if (fellowshipCert) {
      docs.push({
        filename: fellowshipCert.split('/').pop() || 'Fellowship_Certificate.pdf',
        rawPath: fellowshipCert,
        type: 'Fellowship Certificate'
      });
    }

    if (Array.isArray(member.documents)) {
      member.documents.forEach((doc, index) => {
        const rawUrl = doc.url || doc.filename || doc;
        docs.push({
          filename: typeof rawUrl === 'string' ? rawUrl.split('/').pop() : `Document_${index + 1}`,
          rawPath: rawUrl,
          type: doc.type || `Additional Document ${index + 1}`
        });
      });
    }

    return docs;
  };

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading member details...</div>;
  if (error && !member) return <div style={{ padding: '2rem', color: 'red', textAlign: 'center' }}>{error}</div>;

  const firstName = getField(member, 'firstName', 'firstname', 'first_name');
  const middleName = getField(member, 'middleName', 'middlename', 'middle_name');
  const lastName = getField(member, 'lastName', 'lastname', 'last_name');
  const fullName = `${firstName} ${middleName} ${lastName}`.replace(/\s+/g, ' ').trim() || getField(member, 'name', 'fullname') || 'N/A';

  const email = getField(member, 'email') || 'N/A';
  const phone = getField(member, 'phoneNumber', 'phonenumber', 'phone_number', 'phone') || 'N/A';
  const specialty = getField(member, 'areaOfSpecialty', 'areaofspecialty', 'area_of_specialty', 'specialty') || 'N/A';
  const membershipNumber = getField(member, 'membershipNumber', 'membershipnumber') || 'N/A';
  const membershipType = (getField(member, 'membershipType', 'membershiptype') || 'new').toUpperCase();
  const yearQualifiedMBBS = getField(
    member,
    'yearQualifiedMBBS',
    'yearqualifiedmbbs',
    'year_qualified_mbbs',
    'qualificationYear',
    'qualificationyear',
    'mbbsYear'
  ) || 'N/A';
  const mdcnReg = getField(member, 'mdcnRegistrationNumber', 'mdcnregistrationnumber', 'mdcn_registration_number') || 'N/A';
  const streetAddress = getField(member, 'streetAddress', 'streetaddress', 'street_address') || 'N/A';
  const currentPractice = getField(member, 'currentPractice', 'currentpractice', 'current_practice') || 'N/A';
  const isActive = member?.isActive ?? member?.isactive ?? true;

  const documents = getDocuments();

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '2rem', fontFamily: 'Arial, sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <h1 style={{ color: '#006400', margin: 0 }}>Member Profile</h1>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            onClick={() => setIsEditing(!isEditing)}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: isEditing ? '#6c757d' : '#006400',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            {isEditing ? 'Cancel Edit' : 'Edit Member'}
          </button>
          <button
            onClick={() => navigate('/members')}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#e9ecef',
              color: '#333',
              border: '1px solid #ced4da',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Back to List
          </button>
        </div>
      </div>

      {error && (
        <div style={{ backgroundColor: '#f8d7da', color: '#721c24', padding: '1rem', borderRadius: '4px', marginBottom: '1.5rem' }}>
          {error}
        </div>
      )}

      {successMessage && (
        <div style={{ backgroundColor: '#d4edda', color: '#155724', padding: '1rem', borderRadius: '4px', marginBottom: '1.5rem' }}>
          {successMessage}
        </div>
      )}

      {/* READ-ONLY DISPLAY VIEW */}
      {!isEditing ? (
        <div>
          {/* Header Card */}
          <div style={{ backgroundColor: '#f8f9fa', padding: '1.5rem', borderRadius: '8px', marginBottom: '2rem', border: '1px solid #dee2e6', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
            <div>
              <span style={{ fontSize: '0.85rem', color: '#666', display: 'block' }}>Membership Number</span>
              <strong style={{ fontSize: '1.2rem', color: '#006400' }}>{membershipNumber}</strong>
            </div>
            <div>
              <span style={{ fontSize: '0.85rem', color: '#666', display: 'block' }}>Membership Type</span>
              <span style={{ padding: '0.2rem 0.6rem', borderRadius: '12px', fontSize: '0.8rem', fontWeight: 'bold', backgroundColor: '#17a2b8', color: '#fff' }}>
                {membershipType}
              </span>
            </div>
            <div>
              <span style={{ fontSize: '0.85rem', color: '#666', display: 'block' }}>Account Status</span>
              <span style={{ padding: '0.2rem 0.6rem', borderRadius: '12px', fontSize: '0.8rem', fontWeight: 'bold', backgroundColor: isActive ? '#28a745' : '#dc3545', color: '#fff' }}>
                {isActive ? 'ACTIVE' : 'INACTIVE'}
              </span>
            </div>
          </div>

          {/* Details Section */}
          <div style={{ backgroundColor: '#fff', padding: '1.5rem', borderRadius: '8px', border: '1px solid #dee2e6', marginBottom: '1.5rem' }}>
            <h3 style={{ color: '#006400', marginBottom: '1rem', borderBottom: '1px solid #eee', paddingBottom: '0.5rem' }}>Personal Information</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div><strong>Full Name:</strong> {fullName}</div>
              <div><strong>Email:</strong> {email}</div>
              <div><strong>Phone Number:</strong> {phone}</div>
              <div><strong>Area of Specialty:</strong> {specialty}</div>
            </div>
          </div>

          <div style={{ backgroundColor: '#fff', padding: '1.5rem', borderRadius: '8px', border: '1px solid #dee2e6', marginBottom: '1.5rem' }}>
            <h3 style={{ color: '#006400', marginBottom: '1rem', borderBottom: '1px solid #eee', paddingBottom: '0.5rem' }}>Professional Qualifications</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div><strong>Year Qualified (MBBS):</strong> {yearQualifiedMBBS}</div>
              <div><strong>MDCN Registration:</strong> {mdcnReg}</div>
              <div><strong>Current Practice:</strong> {currentPractice}</div>
              <div><strong>Street Address:</strong> {streetAddress}</div>
            </div>
          </div>

          {/* Certificates & Documents Section */}
          <div style={{ backgroundColor: '#fff', padding: '1.5rem', borderRadius: '8px', border: '1px solid #dee2e6', marginBottom: '1.5rem' }}>
            <h3 style={{ color: '#006400', marginBottom: '1rem', borderBottom: '1px solid #eee', paddingBottom: '0.5rem' }}>Certificates & Documents</h3>
            {documents.length > 0 ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
                {documents.map((doc, index) => (
                  <div key={index} style={{ border: '1px solid #ddd', borderRadius: '4px', padding: '1rem', minWidth: '220px', backgroundColor: '#fafafa' }}>
                    <div style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}><strong>{doc.type}</strong></div>
                    <div style={{ fontSize: '0.85rem', marginBottom: '0.75rem', color: '#666', wordBreak: 'break-all' }}>{doc.filename}</div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        type="button"
                        onClick={() => triggerDownload(doc.rawPath, doc.filename, index)}
                        disabled={downloadingIndex === index}
                        style={{
                          color: '#006400',
                          fontSize: '0.85rem',
                          fontWeight: '500',
                          cursor: downloadingIndex === index ? 'not-allowed' : 'pointer',
                          padding: '0.35rem 0.65rem',
                          backgroundColor: '#fff',
                          borderRadius: '4px',
                          border: '1px solid #006400'
                        }}
                      >
                        {downloadingIndex === index ? 'Downloading...' : '📥 Download'}
                      </button>
                      <a
                        href={formatUrl(doc.rawPath)}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: '#333',
                          fontSize: '0.85rem',
                          fontWeight: '500',
                          padding: '0.35rem 0.65rem',
                          backgroundColor: '#e9ecef',
                          borderRadius: '4px',
                          border: '1px solid #ced4da',
                          textDecoration: 'none'
                        }}
                      >
                        👁️ View
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ color: '#666', fontStyle: 'italic' }}>No certificates or documents attached.</div>
            )}
          </div>
        </div>
      ) : (
        /* EDIT FORM VIEW */
        <form onSubmit={handleUpdate} style={{ backgroundColor: '#fff', padding: '2rem', borderRadius: '8px', border: '1px solid #dee2e6', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
          <h3 style={{ color: '#006400', borderBottom: '2px solid #006400', paddingBottom: '0.5rem', marginBottom: '1.5rem' }}>
            Edit Member Information
          </h3>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem', marginBottom: '1.5rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>First Name</label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                style={{ width: '100%', padding: '0.75rem', borderRadius: '4px', border: '1px solid #ced4da' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Last Name</label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                style={{ width: '100%', padding: '0.75rem', borderRadius: '4px', border: '1px solid #ced4da' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Email Address</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                style={{ width: '100%', padding: '0.75rem', borderRadius: '4px', border: '1px solid #ced4da' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Phone Number</label>
              <input
                type="text"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleChange}
                style={{ width: '100%', padding: '0.75rem', borderRadius: '4px', border: '1px solid #ced4da' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Area of Specialty</label>
              <input
                type="text"
                name="areaOfSpecialty"
                value={formData.areaOfSpecialty}
                onChange={handleChange}
                style={{ width: '100%', padding: '0.75rem', borderRadius: '4px', border: '1px solid #ced4da' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Membership Type</label>
              <select
                name="membershipType"
                value={formData.membershipType}
                onChange={handleChange}
                style={{ width: '100%', padding: '0.75rem', borderRadius: '4px', border: '1px solid #ced4da' }}
              >
                <option value="new">New</option>
                <option value="existing">Existing</option>
                <option value="honorary">Honorary</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Year Qualified (MBBS)</label>
              <input
                type="text"
                name="yearQualifiedMBBS"
                value={formData.yearQualifiedMBBS}
                onChange={handleChange}
                placeholder="e.g. 2015"
                style={{ width: '100%', padding: '0.75rem', borderRadius: '4px', border: '1px solid #ced4da' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>MDCN Registration Number</label>
              <input
                type="text"
                name="mdcnRegistrationNumber"
                value={formData.mdcnRegistrationNumber}
                onChange={handleChange}
                style={{ width: '100%', padding: '0.75rem', borderRadius: '4px', border: '1px solid #ced4da' }}
              />
            </div>
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Street Address</label>
            <input
              type="text"
              name="streetAddress"
              value={formData.streetAddress}
              onChange={handleChange}
              style={{ width: '100%', padding: '0.75rem', borderRadius: '4px', border: '1px solid #ced4da' }}
            />
          </div>

          <div style={{ marginBottom: '2rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Current Practice / Institution</label>
            <input
              type="text"
              name="currentPractice"
              value={formData.currentPractice}
              onChange={handleChange}
              style={{ width: '100%', padding: '0.75rem', borderRadius: '4px', border: '1px solid #ced4da' }}
            />
          </div>

          <button
            type="submit"
            disabled={saving}
            style={{
              backgroundColor: '#006400',
              color: '#fff',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '4px',
              fontSize: '1rem',
              fontWeight: 'bold',
              cursor: saving ? 'not-allowed' : 'pointer'
            }}
          >
            {saving ? 'Saving Changes...' : 'Save Member Updates'}
          </button>
        </form>
      )}
    </div>
  );
};

export default MemberView;